import React, { type ChangeEvent } from 'react';
import i18next from 'i18next';
import {
    Button,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    Stack,
    Switch,
    TextField,
} from '@mui/material';
import styles from './Search.module.scss';
import { Search as SearchIcon, CancelOutlined as CancelOutlinedIcon } from '@mui/icons-material';
import { ReduxUpdater } from '../../../../pdf/ReduxUpdater';
import { showOneWordBeforeHighlight } from '../../../../utils/searchUtils';
import { ApryseSDK } from '../../../../pdf/ApryseSDK';

interface ISearchModeReducerAction {
    flag: number;
    remove?: boolean;
}

function searchModeReducer(flags: number, action: ISearchModeReducerAction): number {
    const { flag, remove } = action;
    return remove ? flags & ~flag : flags | flag;
}

/**
 * Returns the formatted search result content.
 * @param item The item to format.
 */
function formatSearchResult(item: any): React.ReactNode {
    const text = item.ambientStr;
    const completeLength = text.length;
    const textOffset = item.resultStrStart;
    const startIndex = item.resultStrStart;
    const endIndex = item.resultStrEnd;
    const highlightLength = endIndex - startIndex;
    const highlight = text.substring(textOffset, textOffset + highlightLength);
    const spaceIndexArray: number[] = [textOffset];
    let begin = '';
    let end = '';

    /* find the indexes of spaces in text */
    for (let i = 0; i < text.length; i++) {
        if (text[textOffset - i] === ' ') {
            spaceIndexArray.push(textOffset - i);
        }

        if (text[textOffset + i] === ' ') {
            spaceIndexArray.push(textOffset + i);
        }
    }

    const sortedIndexArray = spaceIndexArray.sort((a, b) => a - b);
    /* if there are more than one spaces in the sentence and the sentence does not start with the highlighted search value,
     * show only the word before the highlighted search value
     */
    if (sortedIndexArray.length > 1 && textOffset !== 0) {
        [begin, end] = showOneWordBeforeHighlight(
            textOffset,
            sortedIndexArray,
            text,
            completeLength,
            highlightLength,
        );
    }
    if (sortedIndexArray.length === 1 || textOffset === 0) {
        begin = text.substring(0, textOffset);
        end = text.substring(textOffset + highlightLength);
    }

    return (
        <>
            {begin}
            <span className={styles.resultMatch}>{highlight}</span>
            {end}
        </>
    );
}

/**
 * Component that shows a configurable menu structure in a Microsoft-Office-like structure.
 */
export default () => {
    const { Search } = ApryseSDK.instance.Core;
    const [isSearching, setIsSearching] = React.useState<boolean>(false);
    const [searchText, setSearchText] = React.useState<string>('');
    /** State slice that controls the index of the selected search result. */
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [searchMode, setSearchMode] = React.useReducer(searchModeReducer,
        Search.Mode.AMBIENT_STRING | Search.Mode.HIGHLIGHT,
    );
    const [searchResults, setSearchResults] = React.useState<any[]>([]);

    const resetSearch = React.useCallback(() => {
        setSearchText('');
        setSearchResults([]);
        ApryseSDK.documentViewer.clearSearchResults();
    }, []);

    // Executes the search
    const executeSearch = React.useCallback(async () => {
        ApryseSDK.documentViewer.clearSearchResults();
        const results: any[] = [];
        setIsSearching(true);
        ApryseSDK.documentViewer.textSearchInit(searchText, searchMode, {
            fullSearch: true,
            onResult: result => {
                if (result.resultCode === Search.ResultCode.FOUND) {
                    results.push(result);
                }
            },
            onDocumentEnd: () => {
                setSearchResults(results);
                setIsSearching(false);
            },
        });
    }, [searchText, searchMode]);

    React.useEffect(() => {
        ApryseSDK.documentViewer.setSearchHighlightColors({
            searchResult: 'rgba(255, 0, 0, 0.5)',
            activeSearchResult: 'rgba(0, 255, 0, 0.5)',
        });
        // ToDO: document.addEventListener('keydown', onKeyDownEvent);

        // return () => {
        //     document.removeEventListener('keydown', onKeyDownEvent);
        //     ReduxUpdater.toggleIsOnSearch(false);
        // };
    }, []);

    // ToDO: /* To prevent spacebar from scrolling the page while typing a value to input field
    //  * To be able to use ctrl keys for copy and pasting from/to input field
    //  * To be able to use Arrow Right and Arrow Left keys
    //  */
    // const onKeyDownEvent = (e: KeyboardEvent) => {
    //     if (
    //         (e.key === ' ' || e.ctrlKey || e.key === 'ArrowRight' || e.key === 'ArrowLeft') &&
    //         ReduxUpdater.isOnSearch
    //     ) {
    //         e.stopPropagation();
    //     }
    // };

    const resetButton = React.useMemo(() => {
        if (!searchText || searchResults.length === 0) {
            return null;
        }

        return (
            <Button color="error" onClick={resetSearch}>
                {i18next.t('searchPanel.reset')}
            </Button>
        );
    }, [executeSearch]);

    const renderResults = React.useMemo(() => {
        if (!searchResults.length) {
            return null;
        }

        return searchResults.map((item, idx) => (
            <ListItem key={`bookmarkItem-${idx}`} sx={{ overflow: 'hidden' }}>
                <ListItemButton
                    className={styles.resultItem}
                    onClick={() => {
                        ApryseSDK.documentViewer.displaySearchResult(item);
                    }}
                    selected={selectedIndex === idx}
                >
                    <ListItemIcon className={styles.resultIcon}>[{item.pageNum}]</ListItemIcon>
                    <span className={styles.resultSpan}>{formatSearchResult(item)}</span>
                </ListItemButton>
            </ListItem>
        ));
    }, [searchResults, selectedIndex]);

    // The no-results message
    const noResults = React.useMemo(() => {
        if (isSearching || searchResults.length > 0) {
            return null;
        }
        return <div className={styles.noResults}>{i18next.t('searchPanel.noResults')}</div>;
    }, [isSearching, searchResults]);

    const onSearchPatternChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        e.preventDefault();
        const inputValue = e.target.value;
        setSearchText(inputValue);
    };

    const onSearchFieldEnter = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            executeSearch();
        }

        if (e.key === 'Escape') {
            resetSearch();
        }
    };

    const menu = (
        <Stack className={styles.sidebarMenuArea} direction="column" spacing={1} pl={2} pr={2}>
            <TextField
                sx={{
                    '& .MuiInputBase-root': {
                        paddingRight: searchText ? '2px' : '14px',
                    },
                }}
                autoFocus
                placeholder={i18next.t('searchPanel.placeholder')}
                value={searchText}
                onChange={onSearchPatternChange}
                onKeyDown={onSearchFieldEnter}
                InputLabelProps={{
                    shrink: true,
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={executeSearch} edge="end">
                                <SearchIcon />
                            </IconButton>
                            {searchText && (
                                <IconButton onClick={resetSearch}>
                                    <CancelOutlinedIcon />
                                </IconButton>
                            )}
                        </InputAdornment>
                    ),
                }}
            />
            <Stack direction="row">
                <Switch
                    checked={
                        (searchMode & Search.Mode.CASE_SENSITIVE) === Search.Mode.CASE_SENSITIVE
                    }
                    onChange={(_event, checked) =>
                        setSearchMode({
                            flag: Search.Mode.CASE_SENSITIVE,
                            remove: !checked,
                        })
                    }
                />
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {i18next.t('searchPanel.caseSensitive')}
                </span>
            </Stack>
            <Stack direction="row">
                <Switch
                    checked={(searchMode & Search.Mode.WHOLE_WORD) === Search.Mode.WHOLE_WORD}
                    onChange={(_event, checked) =>
                        setSearchMode({
                            flag: Search.Mode.WHOLE_WORD,
                            remove: !checked,
                        })
                    }
                />
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {i18next.t('searchPanel.wholeWord')}
                </span>
            </Stack>
            {resetButton}
        </Stack>
    );

    return (
        <Stack
            onMouseEnter={() => ReduxUpdater.toggleIsOnSearch(true)}
            onMouseLeave={() => ReduxUpdater.toggleIsOnSearch(false)}
        >
            {menu}
            <List
                className={styles.resultList}
                component="nav"
                dense={true}
                sx={{ overflowY: 'scroll' }}
            >
                {renderResults}
                {noResults}
            </List>
        </Stack>
    );
};
